import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Box from '@quintoandar/cozy-core/Box';
import Button from '@quintoandar/cozy-core/Button';
import Typography from '@quintoandar/cozy-core/Typography';

import { AGENTS_API_URL } from 'config';
import LocalMessagesPage from 'components/LocalMessagesPage';
import MyAccountDialog from 'components/MyAccountDialog';
import ROUTES from 'routes/constants';

const LOCAL_BRIDGE_ROUTES = new Set([
  '/favoritos',
  '/meus-dados',
  '/alugar/imovel',
  '/comprar/imovel',
  '/chats/choose-client',
  '/chats/corretor-proprietario',
  '/mensagens',
  '/messages',
]);

const isHouseBridgeRoute = (pathname) =>
  /^\/imovel\/[^/]+(?:\/comprar|\/alugar)?$/.test(pathname);

const isReferralBridgeRoute = (pathname) => /^\/tqc\/[^/]+$/.test(pathname);

const isAgentServiceBridgeRoute = (pathname) =>
  /^\/corretor\/atendimento\/[^/]+$/.test(pathname);

const isOwnerHouseChatRoute = (pathname) =>
  /^\/chats\/corretor\/[^/]+\/proprietario\/[^/]+\/imovel\/[^/]+\/?$/.test(
    pathname,
  );

const isChatThreadRoute = (pathname) =>
  /^\/chats\/(?!choose-client\/?$)(?!corretor-proprietario\/?$)(?!corretor\/)[^/]+\/?$/.test(
    pathname,
  );

const shouldRenderLocalBridgeRoute = (pathname) =>
  LOCAL_BRIDGE_ROUTES.has(pathname) ||
  isHouseBridgeRoute(pathname) ||
  isReferralBridgeRoute(pathname) ||
  isAgentServiceBridgeRoute(pathname) ||
  isOwnerHouseChatRoute(pathname) ||
  isChatThreadRoute(pathname);

export const resolveExternalRouteUrl = ({ pathname }) => {
  if (shouldRenderLocalBridgeRoute(pathname)) return null;

  return null;
};

const fetchAgentsApi = async (path) => {
  const response = await fetch(`${AGENTS_API_URL}${path}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
};

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  currency: 'BRL',
  maximumFractionDigits: 0,
  style: 'currency',
});

const formatCurrency = (value) =>
  currencyFormatter.format(Number(value || 0));

const compactAddress = (address = {}) =>
  [
    address.street || address.address,
    address.number,
    address.complement,
    address.neighborhood || address.region,
    address.city,
  ]
    .filter(Boolean)
    .join(', ');

const contextLabel = (context) => (context === 'SALE' ? 'Compra' : 'Aluguel');

const fieldStyle = {
  border: '1px solid #d5d7de',
  borderRadius: 8,
  boxSizing: 'border-box',
  font: 'inherit',
  minHeight: 44,
  padding: '0 12px',
  width: '100%',
};

const responsiveFormGridStyle = {
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
};

const responsiveDetailGridStyle = {
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
};

const cardStyle = {
  background: '#ffffff',
  border: '1px solid #e5e7ef',
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
};

const chipStyle = {
  background: '#eef2ff',
  borderRadius: 999,
  color: '#2b3fbd',
  display: 'inline-block',
  fontSize: 12,
  fontWeight: 700,
  padding: '4px 10px',
};

const resolveHouseId = (pathname) => {
  const match = pathname.match(/^\/imovel\/([^/]+)/);
  return match?.[1] || null;
};

const resolveReferralCode = (pathname) => {
  const match = pathname.match(/^\/tqc\/([^/]+)/);
  return match?.[1] || null;
};

const resolveServiceCode = (pathname) => {
  const match = pathname.match(/^\/corretor\/atendimento\/([^/]+)/);
  return match?.[1] || null;
};

const resolveOwnerHouseChatParams = (pathname) => {
  const match = pathname.match(
    /^\/chats\/corretor\/([^/]+)\/proprietario\/([^/]+)\/imovel\/([^/]+)\/?$/,
  );

  return {
    agentId: match?.[1] || null,
    ownerId: match?.[2] || null,
    houseId: match?.[3] || null,
  };
};

const resolveChatThreadId = (pathname) => {
  const match = pathname.match(/^\/chats\/([^/]+)\/?$/);
  return match?.[1] || null;
};

const getRouteKind = (pathname) => {
  if (pathname === '/favoritos') return 'favorites';
  if (pathname === '/meus-dados') return 'account';
  if (pathname === '/alugar/imovel') return 'search-rent';
  if (pathname === '/comprar/imovel') return 'search-sale';
  if (pathname === '/chats/choose-client') return 'chat-choose-client';
  if (pathname === '/chats/corretor-proprietario') return 'owner-chats';
  if (pathname === '/mensagens' || pathname === '/messages') return 'messages';
  if (isOwnerHouseChatRoute(pathname)) return 'owner-house-chat';
  if (isChatThreadRoute(pathname)) return 'chat-thread';
  if (isReferralBridgeRoute(pathname)) return 'referral-invite';
  if (isAgentServiceBridgeRoute(pathname)) return 'agent-service';
  if (isHouseBridgeRoute(pathname)) return 'house';
  return 'redirect';
};

const routeNeedsData = (routeKind) =>
  [
    'favorites',
    'house',
    'referral-invite',
    'search-rent',
    'search-sale',
  ].includes(routeKind);

const getHousePrice = (house, businessContext) =>
  businessContext === 'SALE'
    ? house.salePrice || house.rentPrice
    : house.rentPrice || house.salePrice;

const BridgeShell = ({ children, kicker, title, subtitle, testId }) => (
  <Box
    minHeight="100vh"
    px={[2, 4]}
    py={[3, 4]}
    style={{ background: '#f7f8fb' }}
    data-testid={testId}
  >
    <Box mx="auto" maxWidth={1120}>
      <Box mb={3}>
        {kicker && (
          <Typography level="bodyStrong" kind="lighter" mb={1}>
            {kicker}
          </Typography>
        )}
        <Typography level="title" mb={1}>
          {title}
        </Typography>
        {subtitle && (
          <Typography level="body" kind="lighter">
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  </Box>
);

BridgeShell.propTypes = {
  children: PropTypes.node.isRequired,
  kicker: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  testId: PropTypes.string.isRequired,
};

const SearchControls = ({
  businessContext,
  maxPrice,
  minimumBedrooms,
  query,
  setMaxPrice,
  setMinimumBedrooms,
  setQuery,
}) => (
  <Box p={2} mb={3} style={cardStyle}>
    <Box display="flex" mb={2} style={{ gap: 8, flexWrap: 'wrap' }}>
      <Button
        href="/alugar/imovel"
        variant={businessContext === 'RENT' ? 'primary' : 'secondary'}
      >
        Alugar
      </Button>
      <Button
        href="/comprar/imovel"
        variant={businessContext === 'SALE' ? 'primary' : 'secondary'}
      >
        Comprar
      </Button>
    </Box>
    <Box
      display="grid"
      gridGap={12}
      style={responsiveFormGridStyle}
    >
      <label>
        <Typography level="caption" mb={1}>
          Cidade, bairro, rua ou código
        </Typography>
        <input
          aria-label="Cidade, bairro, rua ou código"
          style={fieldStyle}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Ex.: Pinheiros, A1001"
        />
      </label>
      <label>
        <Typography level="caption" mb={1}>
          Quartos
        </Typography>
        <select
          aria-label="Quartos"
          style={fieldStyle}
          value={minimumBedrooms}
          onChange={(event) => setMinimumBedrooms(event.target.value)}
        >
          <option value="">Todos</option>
          <option value="1">1+</option>
          <option value="2">2+</option>
          <option value="3">3+</option>
        </select>
      </label>
      <label>
        <Typography level="caption" mb={1}>
          Valor máximo
        </Typography>
        <input
          aria-label="Valor máximo"
          inputMode="numeric"
          style={fieldStyle}
          value={maxPrice}
          onChange={(event) => setMaxPrice(event.target.value)}
          placeholder={businessContext === 'SALE' ? '1500000' : '5000'}
        />
      </label>
    </Box>
  </Box>
);

SearchControls.propTypes = {
  businessContext: PropTypes.string.isRequired,
  maxPrice: PropTypes.string.isRequired,
  minimumBedrooms: PropTypes.string.isRequired,
  query: PropTypes.string.isRequired,
  setMaxPrice: PropTypes.func.isRequired,
  setMinimumBedrooms: PropTypes.func.isRequired,
  setQuery: PropTypes.func.isRequired,
};

const HouseCard = ({ businessContext, house }) => {
  const address = compactAddress(house.address);
  const detailsPath =
    businessContext === 'SALE'
      ? `/imovel/${house.id}/comprar`
      : `/imovel/${house.id}/alugar`;

  return (
    <Box p={0} style={{ ...cardStyle, overflow: 'hidden' }}>
      <Box
        height={164}
        style={{
          background: `#eef2ff url("${house.mainImageUrl || house.image}") center/cover no-repeat`,
        }}
      />
      <Box p={2}>
        <Box mb={1}>
          <span style={chipStyle}>{contextLabel(house.businessContext || businessContext)}</span>
        </Box>
        <Typography level="bodyStrong" mb={1}>
          {address}
        </Typography>
        <Typography level="body" kind="lighter" mb={2}>
          Código {house.shortId || house.id}
        </Typography>
        <Typography level="title" mb={1}>
          {formatCurrency(getHousePrice(house, businessContext))}
        </Typography>
        <Typography level="caption" kind="lighter" mb={2}>
          {house.characteristics?.bedrooms || house.numberOfBedrooms || 0} quartos ·{' '}
          {house.characteristics?.parkingSpaces || house.parkingSlots || 0} vagas
        </Typography>
        <Button href={detailsPath}>Ver imóvel</Button>
      </Box>
    </Box>
  );
};

HouseCard.propTypes = {
  businessContext: PropTypes.string.isRequired,
  house: PropTypes.object.isRequired,
};

const filterHouses = ({ houses, query, minimumBedrooms, maxPrice, businessContext }) => {
  const normalizedQuery = query.trim().toLowerCase();
  const bedrooms = Number(minimumBedrooms || 0);
  const price = Number(String(maxPrice).replace(/\D/g, '') || 0);

  return houses.filter((house) => {
    const address = compactAddress(house.address).toLowerCase();
    const searchable = `${house.id} ${house.shortId || ''} ${address}`.toLowerCase();
    const houseBedrooms = Number(
      house.characteristics?.bedrooms || house.numberOfBedrooms || 0,
    );
    const housePrice = Number(getHousePrice(house, businessContext) || 0);

    return (
      (!normalizedQuery || searchable.includes(normalizedQuery)) &&
      (!bedrooms || houseBedrooms >= bedrooms) &&
      (!price || housePrice <= price)
    );
  });
};

const HouseList = ({ businessContext, houses }) => {
  const [query, setQuery] = useState('');
  const [minimumBedrooms, setMinimumBedrooms] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const filteredHouses = filterHouses({
    businessContext,
    houses,
    maxPrice,
    minimumBedrooms,
    query,
  });

  return (
    <>
      <SearchControls
        businessContext={businessContext}
        maxPrice={maxPrice}
        minimumBedrooms={minimumBedrooms}
        query={query}
        setMaxPrice={setMaxPrice}
        setMinimumBedrooms={setMinimumBedrooms}
        setQuery={setQuery}
      />
      <Box
        display="grid"
        gridGap={16}
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}
      >
        {filteredHouses.map((house) => (
          <HouseCard
            key={house.id}
            businessContext={businessContext}
            house={house}
          />
        ))}
      </Box>
      {filteredHouses.length === 0 && (
        <Box p={3} mt={2} style={cardStyle}>
          <Typography level="bodyStrong">Nenhum imóvel encontrado</Typography>
        </Box>
      )}
    </>
  );
};

HouseList.propTypes = {
  businessContext: PropTypes.string.isRequired,
  houses: PropTypes.array.isRequired,
};

const SearchPage = ({ businessContext, houses }) => (
  <BridgeShell
    kicker="Imóveis"
    title="Buscar imóveis"
    subtitle="Busque imóveis da carteira disponível e abra o detalhe sem sair do APP2."
    testId="local-property-search-page"
  >
    <HouseList businessContext={businessContext} houses={houses} />
  </BridgeShell>
);

SearchPage.propTypes = {
  businessContext: PropTypes.string.isRequired,
  houses: PropTypes.array.isRequired,
};

const FavoritesPage = ({ houses }) => (
  <BridgeShell
    kicker="Imóveis"
    title="Favoritos e listas"
    subtitle="Lista local de imóveis salvos para manter o fluxo acessível no runtime do corretor."
    testId="local-favorites-page"
  >
    <HouseList businessContext="RENT" houses={houses} />
  </BridgeShell>
);

FavoritesPage.propTypes = {
  houses: PropTypes.array.isRequired,
};

const HouseDetailPage = ({ house, pathname }) => {
  const businessContext = pathname.endsWith('/comprar')
    ? 'SALE'
    : house.businessContext || 'RENT';
  const address = compactAddress(house.address);

  return (
    <BridgeShell
      kicker="Detalhe do imóvel"
      title={address || `Imóvel ${house.id}`}
      subtitle={`Código ${house.shortId || house.id}`}
      testId="local-house-detail-page"
    >
      <Box display="grid" gridGap={24} style={responsiveDetailGridStyle}>
        <Box style={{ ...cardStyle, overflow: 'hidden' }}>
          <Box
            height={360}
            style={{
              background: `#eef2ff url("${house.mainImageUrl || house.image}") center/cover no-repeat`,
            }}
          />
        </Box>
        <Box p={3} style={cardStyle}>
          <Box mb={2}>
            <span style={chipStyle}>{contextLabel(businessContext)}</span>
          </Box>
          <Typography level="title" mb={2}>
            {formatCurrency(getHousePrice(house, businessContext))}
          </Typography>
          <Typography level="body" mb={2}>
            {house.characteristics?.bedrooms || house.numberOfBedrooms || 0} quartos ·{' '}
            {house.characteristics?.parkingSpaces || house.parkingSlots || 0} vagas ·{' '}
            {house.characteristics?.area || house.totalArea || 0} m²
          </Typography>
          <Typography level="body" kind="lighter" mb={3}>
            {house.ciq?.entryInfo || house.accessType?.additionalInfo || 'Informações de visita disponíveis no APP2.'}
          </Typography>
          <Box display="flex" style={{ gap: 12, flexWrap: 'wrap' }}>
            <Button href={`/informacoes-de-entrada/${house.id}`}>
              Informações de entrada
            </Button>
            <Button href={`/proposta/comprar/${house.id}`} variant="secondary">
              Criar proposta
            </Button>
          </Box>
        </Box>
      </Box>
    </BridgeShell>
  );
};

HouseDetailPage.propTypes = {
  house: PropTypes.object.isRequired,
  pathname: PropTypes.string.isRequired,
};

const AccountPage = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(true);

  return (
    <BridgeShell
      kicker="Perfil"
      title="Minha conta"
      subtitle="Use o mesmo seletor de perfil da experiência de referência, sem sair do APP2."
      testId="local-account-page"
    >
      <Box p={3} style={cardStyle}>
        <Typography level="bodyStrong" mb={1}>
          Minha conta
        </Typography>
        <Typography level="body" kind="lighter" mb={3}>
          Altere o perfil de acesso ou abra seus dados mantendo a navegação interna.
        </Typography>
        <Button onClick={() => setIsDialogOpen(true)}>Abrir Minha conta</Button>
      </Box>
      <MyAccountDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </BridgeShell>
  );
};

const ReferralInvitePage = ({ referrals, referralCode }) => {
  const referral = referrals.find(
    (item) => String(item.code).toLowerCase() === String(referralCode).toLowerCase(),
  );
  const clientName = referral?.name || 'Cliente indicado';
  const phone = referral?.phone || 'Telefone informado no convite';

  return (
    <BridgeShell
      kicker="Traga seu cliente"
      title="Convite de comprador"
      subtitle="Fluxo TQC aberto dentro do APP2."
      testId="local-tqc-page"
    >
      <Box display="grid" gridGap={16} style={responsiveDetailGridStyle}>
        <Box p={3} style={cardStyle}>
          <Typography level="bodyStrong" mb={1}>
            {clientName}
          </Typography>
          <Typography level="body" kind="lighter" mb={2}>
            Código do convite: {referralCode}
          </Typography>
          <Typography level="body" mb={3}>
            {phone}
          </Typography>
          <Box display="flex" style={{ gap: 12, flexWrap: 'wrap' }}>
            <Button href="/alugar/imovel">Buscar imóveis</Button>
            <Button href="/agenda?flow=schedule" variant="secondary">
              Agendar visita
            </Button>
          </Box>
        </Box>
        <Box p={3} style={cardStyle}>
          <Typography level="title" mb={2}>
            Próximos passos
          </Typography>
          <Typography level="body" kind="lighter" mb={2}>
            O cliente acompanha imóveis, visitas e propostas sem depender de uma página externa.
          </Typography>
          <Typography level="body" kind="lighter">
            Se o comprador já estiver na carteira, ele continua disponível nas páginas de clientes e perfil.
          </Typography>
        </Box>
      </Box>
    </BridgeShell>
  );
};

ReferralInvitePage.propTypes = {
  referralCode: PropTypes.string.isRequired,
  referrals: PropTypes.array.isRequired,
};

const AgentServicePage = ({ serviceCode }) => (
  <BridgeShell
    kicker="Atendimento"
    title="Link de atendimento do corretor"
    subtitle="Página interna para receber clientes vindos do link de atendimento."
    testId="local-agent-service-page"
  >
    <Box display="grid" gridGap={16} style={responsiveDetailGridStyle}>
      <Box p={3} style={cardStyle}>
        <Typography level="bodyStrong" mb={1}>
          Código do atendimento
        </Typography>
        <Typography level="title" mb={3}>
          {serviceCode}
        </Typography>
        <Typography level="body" kind="lighter" mb={3}>
          O cliente pode continuar por busca, visita ou suporte sem sair do APP2.
        </Typography>
        <Box display="flex" style={{ gap: 12, flexWrap: 'wrap' }}>
          <Button href="/comprar/imovel">Buscar imóveis</Button>
          <Button href="/atendimento-online" variant="secondary">
            Atendimento online
          </Button>
        </Box>
      </Box>
      <Box p={3} style={cardStyle}>
        <Typography level="title" mb={2}>
          Como funciona
        </Typography>
        <Typography level="body" kind="lighter" mb={2}>
          O corretor compartilha o link, o cliente acessa a experiência interna e a carteira registra o atendimento.
        </Typography>
        <Typography level="body" kind="lighter">
          As próximas ações continuam conectadas aos fluxos de agenda, propostas e clientes.
        </Typography>
      </Box>
    </Box>
  </BridgeShell>
);

AgentServicePage.propTypes = {
  serviceCode: PropTypes.string.isRequired,
};

const LoadingState = () => (
  <BridgeShell
    kicker="Carregando"
    title="Abrindo página"
    subtitle="Buscando os dados no APP2."
    testId="local-bridge-loading"
  >
    <Box p={3} style={cardStyle}>
      <Typography level="body">Carregando...</Typography>
    </Box>
  </BridgeShell>
);

const ErrorState = ({ message }) => (
  <BridgeShell
    kicker="Erro"
    title="Não foi possível abrir esta página"
    subtitle="Tente novamente ou volte para a agenda."
    testId="local-bridge-error"
  >
    <Box p={3} style={cardStyle}>
      <Typography level="body" mb={2}>
        {message}
      </Typography>
      <Button href={ROUTES.AGENDA}>Voltar para agenda</Button>
    </Box>
  </BridgeShell>
);

ErrorState.propTypes = {
  message: PropTypes.string.isRequired,
};

const LocalRouteFallback = ({ location }) => {
  const pathname = location?.pathname || window.location.pathname;
  const routeKind = getRouteKind(pathname);
  const [state, setState] = useState({
    clients: [],
    error: null,
    house: null,
    houses: [],
    loading:
      shouldRenderLocalBridgeRoute(pathname) && routeNeedsData(routeKind),
    referrals: [],
  });

  useEffect(() => {
    if (
      !shouldRenderLocalBridgeRoute(pathname) ||
      !routeNeedsData(routeKind)
    ) {
      return;
    }

    let isCurrent = true;
    const businessContext = routeKind === 'search-sale' ? 'SALE' : 'RENT';

    const load = async () => {
      try {
        setState((current) => ({ ...current, error: null, loading: true }));
        const houseId = resolveHouseId(pathname);

        if (routeKind === 'house' || routeKind === 'owner-house-chat') {
          const resolvedHouseId =
            routeKind === 'owner-house-chat'
              ? resolveOwnerHouseChatParams(pathname).houseId
              : houseId;
          const house = await fetchAgentsApi(`/v2/houses/${resolvedHouseId}`);
          if (isCurrent) {
            setState({
              clients: [],
              error: null,
              house,
              houses: [],
              loading: false,
              referrals: [],
            });
          }
          return;
        }

        if (routeKind === 'chat-choose-client') {
          const clients = await fetchAgentsApi('/agents/all-clients?page=0&perPage=24');
          if (isCurrent) {
            setState({
              clients: Array.isArray(clients) ? clients : [],
              error: null,
              house: null,
              houses: [],
              loading: false,
              referrals: [],
            });
          }
          return;
        }

        if (routeKind === 'referral-invite') {
          const referrals = await fetchAgentsApi('/agents/lead-referral?page=1&size=50');
          if (isCurrent) {
            setState({
              clients: [],
              error: null,
              house: null,
              houses: [],
              loading: false,
              referrals: Array.isArray(referrals) ? referrals : [],
            });
          }
          return;
        }

        const query =
          routeKind === 'favorites'
            ? '/v2/houses?pageSize=24'
            : `/v2/houses?businessContext=${businessContext}&pageSize=24`;
        const response = await fetchAgentsApi(query);

        if (isCurrent) {
          setState({
            clients: [],
            error: null,
            house: null,
            houses: response.data || [],
            loading: false,
            referrals: [],
          });
        }
      } catch (error) {
        if (isCurrent) {
          setState({
            clients: [],
            error: error.message || 'Erro inesperado',
            house: null,
            houses: [],
            loading: false,
            referrals: [],
          });
        }
      }
    };

    load();

    return () => {
      isCurrent = false;
    };
  }, [pathname, routeKind]);

  if (shouldRenderLocalBridgeRoute(pathname)) {
    if (state.loading) return <LoadingState />;
    if (state.error) return <ErrorState message={state.error} />;
    if (routeKind === 'account') return <AccountPage />;
    if (routeKind === 'referral-invite') {
      return (
        <ReferralInvitePage
          referralCode={resolveReferralCode(pathname) || ''}
          referrals={state.referrals}
        />
      );
    }
    if (routeKind === 'agent-service') {
      return <AgentServicePage serviceCode={resolveServiceCode(pathname) || ''} />;
    }
    if (routeKind === 'chat-choose-client') {
      return <LocalMessagesPage mode="clients" />;
    }
    if (routeKind === 'owner-chats') {
      return <LocalMessagesPage mode="owners" />;
    }
    if (routeKind === 'owner-house-chat') {
      const chatParams = resolveOwnerHouseChatParams(pathname);
      return (
        <LocalMessagesPage houseId={chatParams.houseId} mode="ownerHouse" />
      );
    }
    if (routeKind === 'chat-thread') {
      return (
        <LocalMessagesPage
          mode="thread"
          threadId={resolveChatThreadId(pathname)}
        />
      );
    }
    if (routeKind === 'messages') return <LocalMessagesPage mode="messages" />;
    if (routeKind === 'favorites') return <FavoritesPage houses={state.houses} />;
    if (routeKind === 'search-sale') {
      return <SearchPage businessContext="SALE" houses={state.houses} />;
    }
    if (routeKind === 'search-rent') {
      return <SearchPage businessContext="RENT" houses={state.houses} />;
    }
    if (routeKind === 'house' && state.house) {
      return <HouseDetailPage house={state.house} pathname={pathname} />;
    }
  }

  return (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      minHeight="100vh"
      justifyContent="center"
      mx="auto"
      maxWidth={480}
      p={3}
      textAlign="center"
      data-testid="local-route-unavailable"
    >
      <Typography level="title" mb={2}>
        Página interna indisponível
      </Typography>
      <Typography level="body" kind="lighter" mb={3}>
        Não encontramos uma superfície local para esta rota.
      </Typography>
      <Button
        onClick={() =>
          window.location.replace(ROUTES.AGENDA)
        }
      >
        Voltar para agenda
      </Button>
    </Box>
  );
};

LocalRouteFallback.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
};

export default LocalRouteFallback;
